import { useQuery, UseQueryOptions, QueryKey } from '@tanstack/react-query';
import { useApiAuth } from '@/hooks/use-api-auth';

/**
 * A wrapper around useQuery that automatically waits for auth to be ready.
 *
 * Use this hook for any query that requires authentication. It prevents
 * queries from executing before the auth token getter is initialized,
 * avoiding 401 errors on initial load.
 *
 * @example
 * export function useProperties() {
 *   return useAuthQuery({
 *     queryKey: queryKeys.properties.all(),
 *     queryFn: api.getProperties,
 *   })
 * }
 *
 * @example
 * // With additional enabled condition
 * export function useProperty(id: string) {
 *   return useAuthQuery({
 *     queryKey: queryKeys.properties.detail(id),
 *     queryFn: () => api.getProperty(id),
 *     enabled: !!id,  // Combined with isAuthReady automatically
 *   })
 * }
 */
export function useAuthQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const { isAuthReady } = useApiAuth();

  return useQuery({
    ...options,
    enabled: isAuthReady && (options.enabled ?? true),
  });
}
